import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { Dispatch, PropsWithChildren, RefCallback, SetStateAction } from 'react';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useSaveForLater } from '../../../bff-api/customers/useSaveForLater';
import { leftMergeArrays } from '../../../components/utils';
import {
    getBopisProductItems,
    getCulinaryProductItems,
    getDropShipProductItems,
    getEGiftCardProductItems,
    getPhysicalGiftCardProductItems,
    getPostPurchaseWarrantyProductItems,
    getSameDayDeliveryProductItems,
    getShipItProducts,
    getShippingProtectionItems,
} from '../../../utils/product-utils';
import { isProductInSaveForLater } from '../utils/cart-utils';
import type { ProductItemEditor } from '../../../bff-api/baskets/model';
import type { CartCulinaryProductItem } from '../models/CartCulinaryProductItem';

interface SplitProductItemEditor {
    shippingProtectionProducts: ProductItemEditor[];
    bopisProducts: ProductItemEditor[];
    sameDayDeliveryProducts: ProductItemEditor[];
    dropShipProducts: ProductItemEditor[];
    shippingProducts: ProductItemEditor[];
    culinaryProducts: CartCulinaryProductItem[];
    eGiftCardProducts: ProductItemEditor[];
    physicalGiftCardProducts: ProductItemEditor[];
    postPurchaseWarrantyProducts: ProductItemEditor[];
}

export type CartStateContext = {
    editorProductItems: ProductItemEditor[];
    setEditorProductItems: Dispatch<SetStateAction<ProductItemEditor[]>>;
    cartProductItemsOrder: { itemId: string; productId: string }[];
    itemRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
    refManager: (itemId: string) => RefCallback<HTMLDivElement>;
    headerHeight: number;
    setHeaderHeight: Dispatch<SetStateAction<number>>;
} & SplitProductItemEditor;

const Context = createContext<CartStateContext>({} as CartStateContext);

export const CartStateProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { isLoading, data: basket, isError: isBasketError, error: basketError } = useBasket();
    const [editorProductItems, setEditorProductItems] = useState<ProductItemEditor[]>([]);
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const { data: saveForLaterData } = useSaveForLater();

    const itemRefs = useRef(new Map<string, HTMLDivElement>());
    const refManager = useCallback(
        (itemId: string) => (el: HTMLDivElement) => {
            if (el) {
                itemRefs.current.set(itemId, el);
            } else {
                itemRefs.current.delete(itemId);
            }
        },
        [],
    );

    if (isBasketError) {
        throw basketError;
    }

    // FIXME: This useEffect should be the select option from useBasket, but it is already being used internally
    useEffect(() => {
        if (!isLoading && basket) {
            setEditorProductItems((prev) => {
                const mergedProductItems = leftMergeArrays(
                    basket?.productItems ?? [],
                    prev.map((p) => ({
                        itemId: p.itemId,
                        quantityEditor: p.quantityEditor,
                    })),
                    'itemId',
                );

                // Make sure that we preserve the shape of the editor fields (quantityEditor, etc)
                return mergedProductItems.map((ma) => ({
                    ...ma,
                    quantityEditor: 'quantityEditor' in ma ? (ma.quantityEditor ?? 0) : ma.quantity,
                    savedForLater: isProductInSaveForLater(ma.productId, saveForLaterData),
                }));
            });
        }
    }, [isLoading, basket, saveForLaterData]);

    const splitProductItems: SplitProductItemEditor = useMemo(
        () => ({
            shippingProducts: getShipItProducts(editorProductItems),
            dropShipProducts: getDropShipProductItems(editorProductItems),
            culinaryProducts: getCulinaryProductItems(editorProductItems),
            bopisProducts: getBopisProductItems(editorProductItems),
            sameDayDeliveryProducts: getSameDayDeliveryProductItems(editorProductItems),
            eGiftCardProducts: getEGiftCardProductItems(editorProductItems),
            physicalGiftCardProducts: getPhysicalGiftCardProductItems(editorProductItems), // These lines are also included in shippingProducts
            postPurchaseWarrantyProducts: getPostPurchaseWarrantyProductItems(editorProductItems),
            shippingProtectionProducts: getShippingProtectionItems(editorProductItems),
        }),
        [editorProductItems],
    );

    const cartProductItemsOrder: { itemId: string; productId: string }[] = useMemo(
        () =>
            [
                ...splitProductItems.shippingProducts,
                ...splitProductItems.shippingProtectionProducts,
                ...splitProductItems.bopisProducts,
                ...splitProductItems.sameDayDeliveryProducts,
                ...splitProductItems.culinaryProducts,
                ...splitProductItems.eGiftCardProducts,
                ...splitProductItems.postPurchaseWarrantyProducts,
            ].map(({ itemId, productId }) => ({ itemId, productId })),
        [splitProductItems],
    );

    const ctx = useMemo<CartStateContext>(
        (): CartStateContext => ({
            editorProductItems,
            setEditorProductItems,
            cartProductItemsOrder,
            ...splitProductItems,
            itemRefs,
            refManager,
            headerHeight,
            setHeaderHeight,
        }),
        [
            editorProductItems,
            setEditorProductItems,
            cartProductItemsOrder,
            splitProductItems,
            refManager,
            headerHeight,
            setHeaderHeight,
        ],
    );

    return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const useCartState = (): CartStateContext => {
    const ctx = useContext(Context);
    if (!ctx) {
        throw new Error('useCartState must be used within CartStateProvider');
    }
    return ctx;
};
